import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { colorWithAlpha } from '../../../common/color';
import TaskBlock from './TaskBlock';
import WorkflowOccurrenceTrack from './WorkflowOccurrenceTrack';

const DAY_MINUTES = 24 * 60;

function buildWorkflowRowKey(entity, rowId) {
  if (!entity || rowId === null || rowId === undefined || rowId === '') {
    return '';
  }

  return `${entity}:${String(rowId)}`;
}

function formatShiftDelta(deltaMinutes) {
  const delta = Number(deltaMinutes) || 0;
  return delta > 0 ? `+${delta} min` : `${delta} min`;
}

function getMinutes(value, timeStringToMinutes) {
  const minutes = timeStringToMinutes(value);
  return Number.isFinite(minutes) ? minutes : null;
}

function buildShiftPreviewTone(color) {
  const workflowColor = color || '#ffffff';

  return {
    '--workflow-shift-preview-track-border': colorWithAlpha(
      workflowColor,
      0.82,
      'rgba(255, 255, 255, 0.82)'
    ),
    '--workflow-shift-preview-track-bg': colorWithAlpha(
      workflowColor,
      0.18,
      'rgba(255, 255, 255, 0.1)'
    ),
    '--workflow-shift-preview-task-bg': colorWithAlpha(
      workflowColor,
      0.58,
      'rgba(255, 255, 255, 0.58)'
    ),
    '--workflow-shift-preview-task-border': colorWithAlpha(
      workflowColor,
      0.86,
      'rgba(255, 255, 255, 0.86)'
    ),
  };
}

export default function WorkflowRow({
  wf,
  zoom,
  timelineWidthPx,
  baseScale,
  timeStringToMinutes,
  timeToPixels,
  onTimelineHoverChange,
  onTimelineContextMenu,
  onWorkflowShiftDragStart,
  onWorkflowSelectionToggle,
  workflowShiftDrag = null,
  isSelected = false,
  isSelectionPreview = false,
  selectionModeActive = false,
  activeHoverKind = null,
  activeHoverWorkflowId = null,
  activeHoverOccurrenceId = null,
  activeHoverForceWorkflowLabel = false,
  isNewlyInserted = false,
  isToday = false,
  currentMinute = null,
}) {
  const isRule = wf.rule_id !== null && wf.rule_id !== undefined;
  const hoverIcon = isRule ? 'bi bi-arrow-repeat' : 'bi bi-calendar-event';
  const isOverlayHoverActive = activeHoverWorkflowId === wf.id;
  const isTaskHoverActive = activeHoverKind === 'task' && activeHoverWorkflowId === wf.id;
  const isSelectionHighlighted = isSelected || isSelectionPreview;
  const shouldForceWorkflowHoverLabel = (
    (activeHoverKind === 'occurrence' || activeHoverKind === 'workflow-shift')
    && activeHoverWorkflowId === wf.id
    && activeHoverForceWorkflowLabel
  );

  const entity = isRule ? 'regular-workflows' : 'day-workflows';
  const rowId = isRule ? wf.rule_id : wf.day_workflow_id;
  const rowKey = buildWorkflowRowKey(entity, rowId);
  const activeShiftItem = workflowShiftDrag?.isActive
    ? (
      (workflowShiftDrag.items || []).find((item) => item.rowKey === rowKey)
      || (workflowShiftDrag.rowKey === rowKey ? workflowShiftDrag : null)
    )
    : null;
  const activeShiftDrag = activeShiftItem
    ? {
      ...workflowShiftDrag,
      ...activeShiftItem,
      rowKey,
      color: activeShiftItem.color || workflowShiftDrag.color,
    }
    : null;
  const activeShiftOccurrence = activeShiftDrag
    ? (wf.occurrences || []).find((occurrence) => (
      String(occurrence.occurrence_id) === String(activeShiftDrag.occurrenceId)
    ))
    : null;
  const activeShiftPreview = activeShiftOccurrence
    ? buildShiftPreview({
      occurrence: activeShiftOccurrence,
      tasks: wf.tasks || [],
      deltaMinutes: activeShiftDrag.deltaMinutes,
      timeStringToMinutes,
      timeToPixels,
      zoom,
    })
    : null;

  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: String(wf.id),
  });

  const style = {
    width: `${timelineWidthPx}px`,
    transform: transform
      ? CSS.Transform.toString({
        ...transform,
        x: 0,
      })
      : undefined,
    transition,
    zIndex: isDragging ? 70 : undefined,
  };

  const handleDragHandleMouseDown = React.useCallback((event) => {
    // Keep keyboard focus intact, but prevent mouse focus from snapping the
    // horizontal timeline scroll position when the drag handle is pressed.
    event.preventDefault();
  }, []);

  const handleDragHandleContextMenu = React.useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
  }, []);

  const handleOccurrenceShiftDragStart = React.useCallback((event, occurrence) => {
    onWorkflowShiftDragStart?.(event, {
      occurrence,
      workflowRenderId: wf.id,
      workflowId: wf.workflow_id ?? null,
      workflowName: wf.name,
      workflowType: isRule ? 'regular' : 'day',
      workflowEntity: entity,
      workflowRowId: rowId,
      timeMode: wf.time_mode ?? 'start',
      anchorTime: wf.anchor_time ?? wf.start_time ?? null,
      color: wf.color,
    });
  }, [
    entity,
    isRule,
    onWorkflowShiftDragStart,
    rowId,
    wf.anchor_time,
    wf.color,
    wf.id,
    wf.name,
    wf.start_time,
    wf.time_mode,
    wf.workflow_id,
  ]);

  const handleOccurrenceSelectionToggle = React.useCallback((detail) => {
    onWorkflowSelectionToggle?.({
      ...detail,
      occurrence: detail?.occurrence,
      workflowRenderId: wf.id,
      workflowName: wf.name,
      workflowType: isRule ? 'regular' : 'day',
      workflowEntity: entity,
      workflowRowId: rowId,
      workflowRowKey: rowKey,
      color: wf.color,
    });
  }, [entity, isRule, onWorkflowSelectionToggle, rowId, rowKey, wf.color, wf.id, wf.name]);

  return (
    <div
      ref={setNodeRef}
      className={[
        'workflow-row',
        isOverlayHoverActive ? 'is-overlay-hover-active' : '',
        shouldForceWorkflowHoverLabel ? 'is-force-workflow-hover-label-visible' : '',
        isNewlyInserted ? 'is-newly-inserted' : '',
        isDragging ? 'is-dragging' : '',
        isSelectionHighlighted ? 'is-selected' : '',
        isSelectionPreview ? 'is-selection-preview' : '',
        activeShiftDrag ? 'is-workflow-shift-dragging' : '',
      ].filter(Boolean).join(' ')}
      style={style}
      data-workflow-row="true"
      data-testid="timeline-workflow-row"
      data-workflow-id={wf.workflow_id ?? wf.id}
      data-workflow-name={wf.name}
      data-workflow-type={isRule ? 'regular' : 'day'}
      data-workflow-entity={entity}
      data-workflow-row-id={rowId}
      data-workflow-row-key={rowKey}
    >
      <div className="workflow-sticky">
        <div className="workflow-row-gutter">
          <button
            ref={setActivatorNodeRef}
            type="button"
            className="workflow-row-drag-handle"
            data-testid="timeline-workflow-row-drag-handle"
            title="Reihenfolge ändern"
            aria-label="Reihenfolge ändern"
            onMouseDown={handleDragHandleMouseDown}
            onContextMenu={handleDragHandleContextMenu}
            {...attributes}
            {...listeners}
          >
            <i className="bi bi-grip-vertical" aria-hidden="true" />
          </button>
        </div>

        <div className="workflow-hover-label">
          <i className={hoverIcon} />
          <span>{wf.name}</span>
        </div>
      </div>

      <div className="workflow-tasks" style={{ width: `${timelineWidthPx}px` }}>
        {(wf.occurrences || []).map((occurrence) => (
          <WorkflowOccurrenceTrack
            key={occurrence.occurrence_id}
            occurrence={occurrence}
            workflowId={wf.id}
            workflowName={wf.name}
            workflowRowKey={rowKey}
            workflowEntity={entity}
            workflowRowId={rowId}
            color={wf.color}
            iconClassName={hoverIcon}
            zoom={zoom}
            timeStringToMinutes={timeStringToMinutes}
            timeToPixels={timeToPixels}
            isTaskHoverActive={isTaskHoverActive}
            isOccurrenceHoverActive={activeHoverKind === 'occurrence' && activeHoverOccurrenceId === occurrence.occurrence_id}
            isSelected={isSelectionHighlighted}
            isSelectionPreview={isSelectionPreview}
            selectionModeActive={selectionModeActive}
            onHoverChange={onTimelineHoverChange}
            onShiftDragStart={handleOccurrenceShiftDragStart}
            onSelectionToggle={handleOccurrenceSelectionToggle}
          />
        ))}

        {(wf.tasks || []).map((task, idx) => (
          <TaskBlock
            key={task.task_id ?? `${wf.id}:${task.start}:${task.end}:${task.name}:${idx}`}
            task={task}
            wf={wf}
            idx={idx}
            zoom={zoom}
            baseScale={baseScale}
            timeStringToMinutes={timeStringToMinutes}
            timeToPixels={timeToPixels}
            onHoverChange={onTimelineHoverChange}
            onTimelineContextMenu={onTimelineContextMenu}
            workflowType={isRule ? 'regular' : 'day'}
            workflowEntity={entity}
            workflowRowId={rowId}
            isToday={isToday}
            currentMinute={currentMinute}
          />
        ))}

        {activeShiftPreview ? (
          <div
            className="workflow-shift-preview"
            style={buildShiftPreviewTone(activeShiftDrag.color || wf.color)}
            data-testid="timeline-workflow-shift-preview"
            data-workflow-row-key={rowKey}
            aria-hidden="true"
          >
            <div
              className="workflow-shift-preview-track"
              style={{
                left: `${activeShiftPreview.track.left}px`,
                width: `${activeShiftPreview.track.width}px`,
              }}
            />

            {activeShiftPreview.tasks.map((taskPreview) => (
              <div
                key={taskPreview.key}
                className="workflow-shift-preview-task"
                style={{
                  left: `${taskPreview.left}px`,
                  width: `${taskPreview.width}px`,
                }}
              />
            ))}

            <span
              className="workflow-shift-preview-badge"
              style={{ left: `${activeShiftPreview.badgeLeft}px` }}
            >
              {formatShiftDelta(activeShiftDrag.deltaMinutes)}
            </span>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function buildShiftPreview({
  occurrence,
  tasks,
  deltaMinutes,
  timeStringToMinutes,
  timeToPixels,
  zoom,
}) {
  const occurrenceStart = getMinutes(occurrence.start, timeStringToMinutes);
  const occurrenceEnd = getMinutes(occurrence.end, timeStringToMinutes);
  if (occurrenceStart === null || occurrenceEnd === null || occurrenceEnd <= occurrenceStart) {
    return null;
  }

  const shiftedOccurrenceStart = Math.max(0, Math.min(DAY_MINUTES, occurrenceStart + deltaMinutes));
  const shiftedOccurrenceEnd = Math.max(0, Math.min(DAY_MINUTES, occurrenceEnd + deltaMinutes));
  const trackLeft = timeToPixels(shiftedOccurrenceStart, zoom);
  const trackWidth = Math.max(1, timeToPixels(shiftedOccurrenceEnd, zoom) - trackLeft);
  const taskPreviews = (tasks || [])
    .map((task, index) => {
      const taskStart = getMinutes(task.start, timeStringToMinutes);
      const taskEnd = getMinutes(task.end, timeStringToMinutes);
      if (taskStart === null || taskEnd === null) {
        return null;
      }

      const clippedStart = Math.max(taskStart, occurrenceStart);
      const clippedEnd = Math.min(taskEnd, occurrenceEnd);
      if (clippedEnd <= clippedStart) {
        return null;
      }

      const shiftedTaskStart = Math.max(0, Math.min(DAY_MINUTES, clippedStart + deltaMinutes));
      const shiftedTaskEnd = Math.max(0, Math.min(DAY_MINUTES, clippedEnd + deltaMinutes));
      const left = timeToPixels(shiftedTaskStart, zoom);
      return {
        key: task.task_id ?? `${task.name ?? 'task'}:${index}`,
        left,
        width: Math.max(1, timeToPixels(shiftedTaskEnd, zoom) - left),
      };
    })
    .filter(Boolean);

  return {
    track: {
      left: trackLeft,
      width: trackWidth,
    },
    tasks: taskPreviews,
    badgeLeft: Math.min(
      timeToPixels(DAY_MINUTES, zoom) - 92,
      Math.max(8, trackLeft + 8),
    ),
  };
}
